import { useEffect, useState } from 'react'
import { useSelector } from 'src/utils/store'
import { actions } from '../../SwapOrReturnState'
import { useDispatch } from 'react-redux'
import { handleOnKeyDown } from 'src/utils/Form'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'
import { useFieldArray, useForm } from 'react-hook-form'
import {
  ActionModalTypes,
  Product,
  SwapOrReturnData,
  SwapOrReturnModalData,
  SwapOrReturnTypeIds,
  SwapOrReturnTypeNames,
  SwapOrReturnTypesEnum,
} from '../../SwapOrReturnTypes'
import AvailableProductsTable from '../AvailableProductsTable/AvailableProductsTable'
import SelectedProductsTable from '../SelectedProductsTable/SelectedProductsTable'
import SwapOrReturnActionsModal from '../SwapOrReturnActionsModal/SwapOrReturnActionsModal'
import InfoRequestCard from '../InfoRequestCard/InfoRequestCard'
import TagPrinterManagerPerBlock from 'src/components/TagPrinterManagerPerBlock/TagPrinterManagerPerBlock'
import SwapOrReturnTag from '../SwapOrReturnTag'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import * as S from './FormCreateSwapOrReturn.styles'

interface ProductFormValues {
  products: Product[]
  invoiceKey: string
  searchTerm: string
}

interface FormCreateSwapOrReturnProps {
  idRequest?: string
}

const FormCreateSwapOrReturn: React.FC<FormCreateSwapOrReturnProps> = ({ idRequest }) => {
  const [modalData, setModalData] = useState<SwapOrReturnModalData>({ show: false, swapOrReturnData: null })
  const [showTagPrinterModal, setShowTagPrinterModal] = useState(false)
  const [showConfirmModal, setShowConfirm] = useState(false)
  const [idRequestToEdit, setIdRequestToEdit] = useState<number | null>(null)

  const {
    swapOrReturnData, selectedProducts, requestTagInfo, requestTypeId, typeAction,
  } = useSelector((state) => state.swapOrReturn)

  const { availableProductsForCreation, infoReturnOptions } = swapOrReturnData

  const readOnly = () => {
    if (typeAction === 'EDIT') {
      if (requestTypeId === SwapOrReturnTypeIds.SWAP && swapOrReturnData.swap) return swapOrReturnData.swap.readOnly
      if (requestTypeId === SwapOrReturnTypeIds.RETURN && swapOrReturnData.return) return swapOrReturnData.return.readOnly
    }
    return false
  }

  const {
    control,
    setValue,
    setError,
    clearErrors,
    register,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ProductFormValues>({
    defaultValues: {
      products: [],
      invoiceKey: '',
      searchTerm: '',
    },
  })

  const watchSearchTerm = watch('searchTerm')

  const dispatch = useDispatch()

  const history = useHistory()

  const { fields } = useFieldArray({
    control,
    name: 'products',
  })

  useEffect(() => {
    if (idRequest) {
      const convertedRequestId = Number(idRequest)

      if (Number.isNaN(convertedRequestId)) {
        toast.error('ID da solicitação inválido!')
        return
      }

      setIdRequestToEdit(convertedRequestId)
      dispatch(actions.getRequestProductsToEdit(convertedRequestId))
    }
  }, [dispatch, idRequest])

  useEffect(() => {
    setValue('products', selectedProducts)
  }, [selectedProducts, setValue])

  const selectProduct = (product: Product) => dispatch(actions.setSelectProduct(product))

  const allProductsSelected = () => availableProductsForCreation.length === 0 && selectedProducts.length > 0

  const clearModalData = () => setModalData({ show: false, swapOrReturnData: null })

  const getSwapOrReturnData = () => {
    const { invoiceKey } = getValues()

    if (!invoiceKey) {
      setError('invoiceKey', { message: 'Informe a chave da nota fiscal!' })
      return
    }

    dispatch(actions.getDataForRequestCreation({ invoiceKey, setModalData }))
    setValue('invoiceKey', '')
    clearErrors()
  }

  const setSwapOrReturnCreationData = (data: SwapOrReturnData, type: SwapOrReturnTypeNames) => {
    const typeOption = type === SwapOrReturnTypeNames.SWAP ? SwapOrReturnTypesEnum.SWAP.toLowerCase() : SwapOrReturnTypesEnum.RETURN.toLowerCase()

    if (!data.availableProductsForCreation || data.availableProductsForCreation.length === 0) {
      toast.error(`Não há produtos disponíveis para criar uma ${typeOption}!`)
      return
    }

    if (type === SwapOrReturnTypeNames.RETURN && !data.infoReturnOptions?.allowInvoiceReturn) {
      toast.error('Fornecedor não aceita nota fiscal de devolução!')
      return
    }

    if (type === SwapOrReturnTypeNames.SWAP && !data.infoReturnOptions?.allowProductExchange) {
      toast.error('Fornecedor não permite troca de produtos!')
      return
    }

    dispatch(actions.setProperty({ property: 'swapOrReturnData', value: data }))
    dispatch(actions.setProperty({ property: 'requestTypeId', value: type === 'swap' ? SwapOrReturnTypeIds.SWAP : SwapOrReturnTypeIds.RETURN }))
    dispatch(actions.setProperty({ property: 'typeAction', value: 'CREATE' }))
    clearModalData()
  }

  const setSwapOrReturnEditionData = (data: SwapOrReturnData, type: SwapOrReturnTypeNames) => {
    const editionData = type === SwapOrReturnTypeNames.SWAP ? data.swap : data.return

    if (!editionData) return

    const dataWithAvailableForAddition = { ...data, availableProductsForCreation: editionData.availableProductsForAddition }

    const products = editionData.products.map((product) => {
      const totalInputQuantity = product.batches.reduce((acc, item) => acc + (item.inputQuantity || 0), 0)
      const isQuantityMatch = product.inputQuantity === totalInputQuantity
      return { ...product, isQuantityMatch }
    })

    dispatch(actions.setProperty({ property: 'swapOrReturnData', value: dataWithAvailableForAddition }))
    dispatch(actions.setProperty({ property: 'selectedProducts', value: products }))
    dispatch(actions.setProperty({ property: 'requestTypeId', value: type === 'swap' ? SwapOrReturnTypeIds.SWAP : SwapOrReturnTypeIds.RETURN }))
    dispatch(actions.setProperty({ property: 'typeAction', value: 'EDIT' }))
    setIdRequestToEdit(editionData.id)
    clearModalData()
  }

  const handleModalActions = (type: SwapOrReturnTypeNames, operation: ActionModalTypes) => {
    const { swapOrReturnData: data } = modalData
    if (!data) return false

    const setDataFunction = operation === ActionModalTypes.EDIT ? setSwapOrReturnEditionData : setSwapOrReturnCreationData
    setDataFunction(data, type)

    return false
  }

  const hasEmptyInputQuantity = () => selectedProducts.some((product) => product.inputQuantity === undefined)

  const quantityNotMatch = () => selectedProducts.some((product) => !product.isQuantityMatch)

  const disableSaveButton = () => {
    if (selectedProducts.length === 0) return true
    if (hasEmptyInputQuantity()) return true
    if (quantityNotMatch()) return true
    return false
  }

  const closeConfirmModal = () => setShowConfirm(false)

  const clearState = () => dispatch(actions.clearState())

  const goBack = () => {
    history.push('/swap-or-return/manage', { fromInternal: true })
  }

  const onSubmitRequest = () => {
    setShowConfirm(false)

    const { products } = getValues()

    dispatch(actions.createOrUpdateRequest({
      requestTypeId,
      idRequest: typeAction === 'CREATE' ? null : idRequestToEdit,
      invoiceKey: swapOrReturnData.invoiceKey,
      products,
      setShowTagPrinterModal,
    }))
  }

  const tags = requestTagInfo.map((b) => <SwapOrReturnTag obj={{ ...b, printed: false }} />)

  return (
    <S.Container>
      <S.ContainerHeader>
        <S.ContainerInputs>
          <S.CustomTextField
            inputProps={{
              ...register('invoiceKey'),
              'data-testid': 'invoice-key-test-id',
            }}
            id='invoiceKey'
            name='invoiceKey'
            type='text'
            autoComplete='off'
            autoFocus
            label='Chave da nota fiscal'
            fullWidth
            error={!!errors.invoiceKey}
            helperText={errors.invoiceKey?.message}
            onKeyDown={(e) => handleOnKeyDown(e, '', getSwapOrReturnData)}
          />
          <S.CustomTextField
            inputProps={{
              ...register('searchTerm'),
              'data-testid': 'search-term-test-id',
            }}
            id='searchTerm'
            name='searchTerm'
            type='text'
            autoComplete='off'
            label='Buscar por código ou descrição do produto'
            fullWidth
            disabled={availableProductsForCreation.length === 0 && selectedProducts.length === 0}
          />
        </S.ContainerInputs>
        {infoReturnOptions && <InfoRequestCard info={infoReturnOptions} readOnly={readOnly()} />}
      </S.ContainerHeader>
      <S.ContainerTables>
        <AvailableProductsTable
          availableProducts={availableProductsForCreation}
          searchTerm={watchSearchTerm}
          allProductsSelected={allProductsSelected()}
          readOnly={readOnly()}
          onSelect={selectProduct}
        />
        <SelectedProductsTable
          selectedProducts={fields}
          infoReturnOptions={infoReturnOptions}
          searchTerm={watchSearchTerm}
          control={control}
          errors={errors}
          readOnly={readOnly()}
          setError={setError}
          clearErrors={clearErrors}
        />
      </S.ContainerTables>
      <S.ContainerButtons>
        <S.CustomButton
          title='Voltar'
          bgColor='lightBlue5'
          hoverColor='darkBlueHover2'
          letterColor='white'
          data-testid='back-button-test-id'
          onClick={goBack}
        />
        <S.CustomButton
          title='Salvar'
          bgColor='confirm'
          hoverColor='confirmHover'
          letterColor='white'
          disabled={!!errors.products || disableSaveButton() || readOnly()}
          onClick={() => setShowConfirm(true)}
          data-testid='save-button-test-id'
        />
      </S.ContainerButtons>
      {
        modalData.swapOrReturnData && (
          <SwapOrReturnActionsModal
            show={modalData.show}
            swapOrReturnData={modalData.swapOrReturnData}
            onAction={handleModalActions}
            onClose={clearModalData}
          />
        )
      }
      <TagPrinterManagerPerBlock
        tagsToPrint={tags}
        showTagModal={showTagPrinterModal}
        setShowTagModal={setShowTagPrinterModal}
        actionAfterClose={clearState}
        modalTitle={`Impressão de etiquetas de ${requestTypeId === SwapOrReturnTypeIds.SWAP ? 'troca' : 'devolução'}`}
        printLimit={25}
      />
      <ConfirmOrRejectModal
        show={showConfirmModal}
        title='Confirmar operação'
        description={
          `Deseja realmente salvar a ${typeAction === 'CREATE' ? 'criação' : 'edição'} da solicitação de
          ${requestTypeId === SwapOrReturnTypeIds.SWAP ? 'troca' : 'devolução'}?`
        }
        onConfirm={onSubmitRequest}
        onReject={closeConfirmModal}
        onClose={closeConfirmModal}
        dataTestId='confirm-modal-test-id'
      />
    </S.Container>
  )
}

export default FormCreateSwapOrReturn
